/**
 * App-level Cast session host.
 *
 * Owns CastingManager for the SPA lifetime. VideoPlayer attaches/detaches
 * local playback; opening/closing a video must not start or end Cast.
 *
 * Provider-neutral: Chromecast / AirPlay / Roku / Fire TV / Samsung / LG /
 * FutureNative / Screen Mirroring / Simulated / Null — plug the same
 * CastingManager host. Only web-operational providers report available cast
 * devices. Screen Mirror is a separate always-on menu capability.
 */

import configuration from "react-global-configuration";
import { createCastingManager } from "./CastingManager";
import { CastingEvent } from "./CastingEvents";
import { normalizeWhiteLabelCastConfig } from "./CastReceiverContract";

var _session = null;

function readWhiteLabelFromConfig() {
  var raw = {};
  try {
    raw = {
      applicationKey:
        configuration.get("configData.application_key") ||
        configuration.get("configData.app_key") ||
        configuration.get("configData.site_name") ||
        "",
      applicationName:
        configuration.get("configData.site_name") ||
        configuration.get("configData.application_name") ||
        "",
      receiverApplicationId:
        configuration.get("configData.chromecast_receiver_id") ||
        configuration.get("configData.cast_receiver_application_id") ||
        "",
      logo:
        configuration.get("configData.site_logo") ||
        configuration.get("configData.logo") ||
        "",
      rokuChannelId: configuration.get("configData.roku_channel_id") || "",
      fireTvPackageName:
        configuration.get("configData.fire_tv_package_name") || "",
      samsungAppId: configuration.get("configData.samsung_app_id") || "",
      lgAppId: configuration.get("configData.lg_app_id") || "",
      supportedCastProviders: configuration.get(
        "configData.supported_cast_providers"
      ),
      defaultReceiver:
        configuration.get("configData.default_cast_receiver") || "chromecast",
    };
  } catch (e) {
    raw = {};
  }
  return normalizeWhiteLabelCastConfig(raw);
}

function readCastingConfig() {
  var whiteLabel = readWhiteLabelFromConfig();
  return {
    chromecast: true,
    airplay: true,
    roku: true,
    firetv: true,
    samsung: true,
    lg: true,
    future_native: true,
    receiverApplicationId: whiteLabel.receiverApplicationId || undefined,
    whiteLabel: whiteLabel,
    chromecastOptions: whiteLabel.receiverApplicationId
      ? { receiverApplicationId: whiteLabel.receiverApplicationId }
      : {},
  };
}

function readFeatures(overrides) {
  overrides = overrides || {};
  // App-level Cast is capability-on when the session host is created.
  // Nav visibility is gated separately via is_cast_system_enabled.
  return Object.assign(
    {
      casting: true,
      chromecast: true,
      airplay: true,
      roku: true,
      firetv: true,
      samsung: true,
      lg: true,
      future_native: true,
      castingSimulate: !!overrides.castingSimulate,
    },
    overrides
  );
}

function publishGlobal(manager) {
  if (typeof window === "undefined") return;
  window.__TPG_CASTING__ = manager;
  try {
    window.dispatchEvent(new CustomEvent("tpg-casting-changed"));
  } catch (e) {
    /* ignore */
  }
}

/**
 * Ensure a singleton CastingManager exists and is initialized.
 * @param {object} [options]
 * @returns {object} AppCastingSession facade
 */
export function ensureAppCastingSession(options) {
  options = options || {};
  if (_session) {
    if (options.features || options.casting) {
      // Already live — do not rebuild providers mid-session.
    }
    return _session;
  }

  var manager = createCastingManager({
    engine: null,
    tracks: null,
    features: readFeatures(options.features),
    casting: Object.assign(readCastingConfig(), options.casting || {}),
    getMediaDescriptor: function () {
      return {};
    },
  });

  var initPromise = manager.initialize().catch(function () {
    return null;
  });

  function notify() {
    publishGlobal(manager);
  }

  var unsubs = [
    manager.on(CastingEvent.TARGET_CHANGED, notify),
    manager.on(CastingEvent.SESSION_STARTED, notify),
    manager.on(CastingEvent.SESSION_ENDED, notify),
    manager.on(CastingEvent.AVAILABILITY_CHANGED, notify),
    manager.on(CastingEvent.PROVIDERS_CHANGED, notify),
    manager.on(CastingEvent.ERROR, notify),
    manager.on(CastingEvent.STATE_SYNC, function (payload) {
      if (payload && payload.logOnly) return;
      notify();
    }),
  ];

  publishGlobal(manager);

  _session = {
    manager: manager,
    ready: initPromise,

    getManager: function () {
      return manager;
    },

    /**
     * Player mounts — bind local engine without owning session lifecycle.
     */
    attachPlayer: function (binding) {
      return manager.attachPlayback(binding || {});
    },

    /**
     * Player unmounts — keep Cast session; clear local engine refs only.
     */
    detachPlayer: function () {
      return manager.detachPlayback();
    },

    handoffContent: function (media) {
      return manager.handoffContent(media);
    },

    start: function (providerName, deviceId) {
      if (providerName) {
        return manager.start(providerName, deviceId);
      }
      return manager.startPreferred(deviceId);
    },

    startRokuCloudPairing: function (media) {
      return manager.startRokuCloudPairing(media || {});
    },

    startScreenMirror: function () {
      return manager.startScreenMirror
        ? manager.startScreenMirror()
        : Promise.reject(new Error("Screen Mirror is not available"));
    },

    stopScreenMirror: function () {
      return manager.stopScreenMirror
        ? manager.stopScreenMirror()
        : Promise.resolve(null);
    },

    getScreenMirrorSnapshot: function () {
      return manager.getScreenMirrorSnapshot
        ? manager.getScreenMirrorSnapshot()
        : null;
    },

    stop: function () {
      return manager.stop();
    },

    getUiState: function () {
      return manager.getUiState();
    },

    getSessionInfo: function () {
      return manager.getSessionInfo();
    },

    isRemote: function () {
      return manager.isRemote();
    },

    on: function (type, fn) {
      return manager.on(type, fn);
    },

    /**
     * Full teardown (tests / SPA unload). Do not call on video close.
     */
    destroy: function () {
      unsubs.forEach(function (u) {
        if (typeof u === "function") u();
      });
      manager.destroy();
      if (typeof window !== "undefined" && window.__TPG_CASTING__ === manager) {
        window.__TPG_CASTING__ = null;
        try {
          window.dispatchEvent(new CustomEvent("tpg-casting-changed"));
        } catch (e) {
          /* ignore */
        }
      }
      _session = null;
    },
  };

  return _session;
}

export function getAppCastingSession() {
  return _session;
}

/** Test helper — reset singleton between contract runs. */
export function __resetAppCastingSessionForTests() {
  if (_session) {
    _session.destroy();
  }
  _session = null;
}

export default ensureAppCastingSession;
